/**
 * useVoiceAgent — React adapter for VoiceAgentController.
 *
 * This is the ONLY React-aware file in the voice-agent lib. The
 * controller itself is framework-agnostic. A Vue / Svelte / vanilla-JS
 * adapter would look almost identical to this file but call its host
 * framework's reactive primitive instead of useSyncExternalStore.
 *
 * Returns the controller's snapshot + the controller instance itself
 * (for imperative calls like open() / setMicActive()). React's
 * useSyncExternalStore guarantees:
 *   - All consumers re-render in a single batched update on any change.
 *   - getSnapshot() returns a referentially stable value when state has
 *     not actually changed (controller is responsible for that).
 */

import { useSyncExternalStore } from 'react'
import type { VoiceAgentController, VoiceAgentSnapshot } from './VoiceAgentController'

export interface UseVoiceAgentResult extends VoiceAgentSnapshot {
  controller: VoiceAgentController
}

export function useVoiceAgent(controller: VoiceAgentController): UseVoiceAgentResult {
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot, // SSR fallback — same snapshot
  )
  return { ...snapshot, controller }
}
